import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  CameraIcon,
  XMarkIcon,
  TruckIcon,
  PhoneIcon,
  TagIcon,
  MapPinIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  FunnelIcon,
  ChevronRightIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import supabase from './utils/supabaseClient';

const STATUS_META = {
  novo:          { label: 'Novo',           bg: 'bg-brand-100',  text: 'text-brand-700'  },
  contatado:     { label: 'Contatado',      bg: 'bg-accent-100', text: 'text-accent-700' },
  interessado:   { label: 'Interessado',    bg: 'bg-accent-100', text: 'text-accent-700' },
  convidado:     { label: 'Convidado',      bg: 'bg-accent-100', text: 'text-accent-700' },
  rejeitou:      { label: 'Rejeitou',       bg: 'bg-ink-100',    text: 'text-ink-600'    },
  convertido:    { label: 'Convertido',     bg: 'bg-green-100',  text: 'text-green-700'  },
  perdido:       { label: 'Perdido',        bg: 'bg-red-100',    text: 'text-red-700'    },
};

const FILTERS = [
  { value: '',            label: 'Todos' },
  { value: 'novo',        label: 'Novos' },
  { value: 'contatado',   label: 'Contatados' },
  { value: 'interessado', label: 'Interessados' },
  { value: 'convidado',   label: 'Convidados' },
  { value: 'convertido',  label: 'Convertidos' },
];

const fmt = (iso) => iso
  ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—';

const Badge = ({ status }) => {
  const m = STATUS_META[status] ?? STATUS_META.novo;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${m.bg} ${m.text}`}>
      {m.label}
    </span>
  );
};

const AdminLeadsTab = () => {
  const [leads, setLeads]               = useState([]);
  const [loading, setLoading]           = useState(false);
  const [refreshKey, setRefreshKey]     = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]             = useState('');
  const [selected, setSelected]         = useState(null);
  const [editStatus, setEditStatus]     = useState('');
  const [editNotas, setEditNotas]       = useState('');
  const [editPlaca, setEditPlaca]       = useState('');
  const [editTel, setEditTel]           = useState('');
  const [editEnd, setEditEnd]           = useState('');
  const [saving, setSaving]             = useState(false);
  const [imageUrl, setImageUrl]         = useState(null);

  useEffect(() => {
    setLoading(true);
    supabase
      .from('leads_caminhoes')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500)
      .then(({ data, error }) => {
        setLoading(false);
        if (error) { toast.error('Erro ao carregar leads.'); return; }
        setLeads(data ?? []);
      });
  }, [refreshKey]);

  // Sign URL para a imagem do lead selecionado
  useEffect(() => {
    if (!selected) { setImageUrl(null); return; }
    let cancelled = false;
    supabase.storage.from('truck-leads').createSignedUrl(selected.storage_path, 60 * 60).then(({ data }) => {
      if (!cancelled) setImageUrl(data?.signedUrl ?? null);
    });
    return () => { cancelled = true; };
  }, [selected]);

  const selectedId = selected?.id;
  useEffect(() => {
    if (selected) {
      setEditStatus(selected.status);
      setEditNotas(selected.notas ?? '');
      setEditPlaca(selected.placa ?? '');
      setEditTel(selected.telefone ?? '');
      setEditEnd(selected.endereco_aproximado ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const stats = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return {
      total:         leads.length,
      novos:         leads.filter((l) => l.status === 'novo').length,
      em_contato:    leads.filter((l) => ['contatado','interessado','convidado'].includes(l.status)).length,
      convertidos:   leads.filter((l) => l.status === 'convertido').length,
      hoje:          leads.filter((l) => new Date(l.created_at) >= today).length,
    };
  }, [leads]);

  const filtered = useMemo(() => {
    let out = leads;
    if (statusFilter) out = out.filter((l) => l.status === statusFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      out = out.filter((l) =>
        [l.placa, l.telefone, l.cor, l.tipo_veiculo, l.notas, l.caracteristicas]
          .some((v) => v && String(v).toLowerCase().includes(s))
      );
    }
    return out;
  }, [leads, statusFilter, search]);

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    const updates = {
      status:                editStatus,
      notas:                 editNotas.trim() || null,
      placa:                 editPlaca.trim().toUpperCase().replace(/[^A-Z0-9]/g, '') || null,
      telefone:              editTel.trim() || null,
      endereco_aproximado:   editEnd.trim() || null,
      ultima_interacao_em:   new Date().toISOString(),
    };
    const { error } = await supabase.from('leads_caminhoes').update(updates).eq('id', selected.id);
    setSaving(false);
    if (error) { toast.error('Erro ao salvar.'); return; }
    setLeads((arr) => arr.map((l) => l.id === selected.id ? { ...l, ...updates } : l));
    setSelected({ ...selected, ...updates });
    toast.success('Lead atualizado.');
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm('Apagar este lead? A foto também será removida.')) return;
    setSaving(true);

    // Apaga a imagem do storage
    await supabase.storage.from('truck-leads').remove([selected.storage_path]);
    const { error } = await supabase.from('leads_caminhoes').delete().eq('id', selected.id);
    setSaving(false);
    if (error) { toast.error('Erro ao apagar.'); return; }
    setLeads((arr) => arr.filter((l) => l.id !== selected.id));
    setSelected(null);
    toast.success('Lead apagado.');
  };

  const wppLink = (telefone) => {
    if (!telefone) return null;
    const digits = telefone.replace(/\D/g, '');
    if (digits.length < 10) return null;
    const intlNumber = digits.startsWith('55') ? digits : `55${digits}`;
    return `https://wa.me/${intlNumber}?text=${encodeURIComponent('Olá, sou da Fretz. Vi seu caminhão e gostaria de conversar sobre uma parceria.')}`;
  };

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
        {[
          { label: 'Total',         value: stats.total,       color: 'text-ink-900' },
          { label: 'Hoje',          value: stats.hoje,        color: 'text-brand-700' },
          { label: 'Novos',         value: stats.novos,       color: 'text-accent-600' },
          { label: 'Em contato',    value: stats.em_contato,  color: 'text-accent-600' },
          { label: 'Convertidos',   value: stats.convertidos, color: 'text-green-700' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card p-4">
            <p className="text-xs text-ink-500">{label}</p>
            <p className={`text-2xl font-display font-bold mt-1 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      <div className="card p-4 mb-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <FunnelIcon className="h-4 w-4 text-ink-400 shrink-0" />
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition
                ${statusFilter === f.value
                  ? 'bg-brand-gradient text-white shadow-glow'
                  : 'bg-ink-100 text-ink-600 hover:bg-ink-200'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="sm:ml-auto flex items-center gap-2">
          <input
            className="input text-sm w-44"
            type="search"
            placeholder="Placa, tel, notas…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button onClick={() => setRefreshKey((k) => k + 1)} className="btn-ghost text-xs gap-1.5">
            <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Atualizar
          </button>
          <Link to="/truck" className="btn-primary text-xs gap-1.5">
            <CameraIcon className="h-3.5 w-3.5" /> Capturar lead
          </Link>
        </div>
      </div>

      <div className="card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-ink-400">
            <TruckIcon className="h-10 w-10 mx-auto mb-3" />
            <p className="text-sm font-medium">Nenhum lead capturado ainda.</p>
            <Link to="/truck" className="mt-4 btn-primary inline-flex">
              <CameraIcon className="h-4 w-4" /> Capturar primeiro
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 bg-ink-50/70">
                  <th className="text-left px-4 py-3 font-semibold text-ink-500">Capturado</th>
                  <th className="text-left px-4 py-3 font-semibold text-ink-500">Placa</th>
                  <th className="text-left px-4 py-3 font-semibold text-ink-500">Tipo</th>
                  <th className="text-left px-4 py-3 font-semibold text-ink-500">Telefone</th>
                  <th className="text-left px-4 py-3 font-semibold text-ink-500">Status</th>
                  <th className="w-8 px-2" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} onClick={() => setSelected(l)}
                      className="border-b border-ink-50 hover:bg-brand-50/40 cursor-pointer transition">
                    <td className="px-4 py-3 text-ink-700">{fmt(l.created_at)}</td>
                    <td className="px-4 py-3 font-mono text-ink-900">{l.placa || '—'}</td>
                    <td className="px-4 py-3 text-ink-700">
                      {l.tipo_veiculo ? `${l.tipo_veiculo}${l.cor ? ` · ${l.cor}` : ''}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-ink-700">{l.telefone || '—'}</td>
                    <td className="px-4 py-3"><Badge status={l.status} /></td>
                    <td className="px-2 py-3 text-ink-300"><ChevronRightIcon className="h-4 w-4" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-ink-900/40 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <aside className="w-full max-w-lg bg-white shadow-xl flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-ink-100 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs text-ink-400 font-bold uppercase">Lead</p>
                <p className="font-mono font-bold text-brand-700">{selected.placa || '— sem placa —'}</p>
                <p className="text-xs text-ink-500 mt-0.5">{fmt(selected.created_at)}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge status={selected.status} />
                <button onClick={() => setSelected(null)} className="p-1.5 rounded-lg hover:bg-ink-100 text-ink-400">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {imageUrl && (
                <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="block">
                  <img src={imageUrl} alt="Caminhão" className="w-full max-h-72 object-contain rounded-2xl bg-ink-50" />
                </a>
              )}

              <div className="rounded-xl border border-ink-100 divide-y divide-ink-50 text-sm">
                {[
                  ['Tipo',    selected.tipo_veiculo || '—'],
                  ['Cor',     selected.cor || '—'],
                  ['Porte',   selected.porte || '—'],
                  ['Características', selected.caracteristicas || '—'],
                  ['Confiança IA', selected.ai_confidence != null ? `${(selected.ai_confidence * 100).toFixed(0)}%` : '—'],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 px-4 py-2.5">
                    <span className="text-ink-500">{k}</span>
                    <span className="text-ink-900 font-medium text-right break-words">{v}</span>
                  </div>
                ))}
              </div>

              {(selected.exif_lat || selected.exif_taken_at) && (
                <div className="rounded-xl border border-brand-100 bg-brand-50/30 p-4">
                  <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-2">EXIF</p>
                  {selected.exif_lat && selected.exif_lng && (
                    <p className="text-sm flex items-center gap-2">
                      <MapPinIcon className="h-4 w-4 text-brand-600" />
                      <a
                        href={`https://www.google.com/maps?q=${selected.exif_lat},${selected.exif_lng}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-brand-700 underline"
                      >
                        {Number(selected.exif_lat).toFixed(5)}, {Number(selected.exif_lng).toFixed(5)}
                      </a>
                    </p>
                  )}
                  {selected.exif_taken_at && (
                    <p className="text-sm mt-1 text-ink-700">📅 {fmt(selected.exif_taken_at)}</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label flex items-center gap-1" htmlFor="ed-placa">
                    <TagIcon className="h-3.5 w-3.5" /> Placa
                  </label>
                  <input id="ed-placa" className="input font-mono uppercase"
                         value={editPlaca}
                         onChange={(e) => setEditPlaca(e.target.value)} />
                </div>
                <div>
                  <label className="label flex items-center gap-1" htmlFor="ed-tel">
                    <PhoneIcon className="h-3.5 w-3.5" /> Telefone
                  </label>
                  <input id="ed-tel" className="input"
                         value={editTel}
                         onChange={(e) => setEditTel(e.target.value)} />
                </div>
                <div className="col-span-2">
                  <label className="label" htmlFor="ed-end">Endereço aproximado</label>
                  <input id="ed-end" className="input" value={editEnd}
                         onChange={(e) => setEditEnd(e.target.value)} />
                </div>
                <div className="col-span-2">
                  <label className="label" htmlFor="ed-status">Status do pipeline</label>
                  <select id="ed-status" className="input" value={editStatus}
                          onChange={(e) => setEditStatus(e.target.value)}>
                    {Object.entries(STATUS_META).map(([v, m]) => (
                      <option key={v} value={v}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="label" htmlFor="ed-notas">Notas (histórico de contato)</label>
                  <textarea id="ed-notas" className="input min-h-[100px] resize-y"
                            value={editNotas}
                            onChange={(e) => setEditNotas(e.target.value)}
                            placeholder="Ex.: 03/05 ligou, atendeu, ficou de pensar." />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                {wppLink(editTel) && (
                  <a href={wppLink(editTel)} target="_blank" rel="noopener noreferrer"
                     className="btn-secondary gap-2">
                    <ChatBubbleLeftRightIcon className="h-4 w-4" />
                    Abrir WhatsApp
                  </a>
                )}
                <button onClick={handleSave} disabled={saving} className="btn-primary gap-2">
                  {saving ? 'Salvando…' : (<><CheckCircleIcon className="h-4 w-4" /> Salvar alterações</>)}
                </button>
                <button onClick={handleDelete} disabled={saving}
                        className="text-xs text-red-600 hover:text-red-700 underline mt-2">
                  <TrashIcon className="h-3 w-3 inline mr-1" /> Apagar lead e imagem
                </button>
              </div>

              {selected.texto_extraido && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-ink-500">Texto bruto da IA</summary>
                  <pre className="mt-2 p-3 bg-ink-50 rounded-lg whitespace-pre-wrap font-mono text-[11px]">
                    {selected.texto_extraido}
                  </pre>
                </details>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default AdminLeadsTab;
